import { useContext } from "react"

import { faIcon } from "@utils/faIcon"

import DrawerHeader from "@atoms/BottomDrawer/DrawerHeader/DrawerHeader"

import { PopoverContext } from "@molecules/Popover/Popover"

import { useComponentNavigation } from "@organisms/ComponentNavigation/ComponentNavigation.context"

import type { PopoverHeaderButtonProps, PopoverHeaderRootProps, PopoverHeaderTitleProps } from "./PopoverHeader.types"

function PopoverHeaderRoot(props: PopoverHeaderRootProps) {
    const { className, children, showDivider = true } = props

    return (
        <>
            <DrawerHeader.Root className={className} showDivider={showDivider}>
                {children}
            </DrawerHeader.Root>
        </>
    )
}

function PopoverHeaderTitle(props: PopoverHeaderTitleProps) {
    const { title, icon, className, counter, supportText, ...rest } = props

    return (
        <DrawerHeader.Title
            title={title}
            titleIcon={icon}
            titleClassName={className}
            counter={counter}
            supportText={supportText}
            {...rest}
        />
    )
}

function PopoverHeaderBackButton(props: PopoverHeaderButtonProps) {
    const { className, onClick, "aria-label": ariaLabel, isTabbable, icon, backDestination, isPressed } = props
    const { isOpen } = useContext(PopoverContext)
    const { navigate } = useComponentNavigation()

    const handleClick = () => {
        if (backDestination) {
            navigate(backDestination, "left")
        } else if (onClick) {
            onClick()
        }
    }

    return (
        <DrawerHeader.LeftButton
            className={className}
            aria-label={ariaLabel || "Back"}
            icon={icon || faIcon({ name: "arrow-left", style: "regular" })}
            onClick={handleClick}
            isTabbable={isTabbable ?? Boolean(isOpen)}
            isPressed={isPressed}
        />
    )
}

function PopoverHeaderCloseButton(props: PopoverHeaderButtonProps) {
    const { className, onClick, "aria-label": ariaLabel, isTabbable, icon, iconOnActive, isPressed } = props
    const { isOpen, setIsOpen } = useContext(PopoverContext)

    const handleClick = () => {
        if (onClick) {
            onClick()
        } else {
            setIsOpen(false)
        }
    }

    return (
        <DrawerHeader.RightButton
            className={className}
            aria-label={ariaLabel || "Close"}
            icon={icon || faIcon({ name: "xmark", style: "regular" })}
            iconOnActive={iconOnActive || faIcon({ name: "xmark", style: "solid" })}
            onClick={handleClick}
            isTabbable={isTabbable ?? Boolean(isOpen)}
            isPressed={isPressed}
        />
    )
}

export default {
    Root: PopoverHeaderRoot,
    Title: PopoverHeaderTitle,
    LeftSlot: DrawerHeader.LeftSlot,
    BackButton: PopoverHeaderBackButton,
    CloseButton: PopoverHeaderCloseButton,
}
